/**
 * FASE 5 — INTERNAL KNOWLEDGE GRAPH.
 *
 * Fuente única de las RELACIONES editoriales del sitio:
 *   Luis ROC → expertise (hub) → servicio → metodología → caso → artículo → CTA.
 *
 * Reglas:
 * - Solo se referencian rutas que existen (src/utils/routes.ts). Nada inventado.
 * - Los casos se enlazan por slug y solo si son públicos (caseStudiesData).
 * - Un caso nunca se reproduce completo fuera de su página: excerpt + enlace.
 * - El CTA depende de la INTENCIÓN del hub, no es el mismo en todo el sitio.
 */

import type { MoneyPageLink } from "@/data/money-pages/types";

export type Language = 'es' | 'en';

/** ---------------------------------------------------------------
 * 1. CTA POR INTENCIÓN (sección 18 del brief)
 * El backend/formulario es compartido; cambia el mensaje de entrada.
 * --------------------------------------------------------------- */
export type ConversionIntent =
  | 'seo'
  | 'local'
  | 'website'
  | 'branding'
  | 'hispanic'
  | 'amazon'
  | 'googleAds'
  | 'aiSearch'
  | 'b2b';

interface CtaDefinition {
  es: { label: string; to: string };
  en: { label: string; to: string };
}

export const CTA_BY_INTENT: Record<ConversionIntent, CtaDefinition> = {
  seo: {
    es: { label: "Solicitar un diagnóstico SEO", to: "/diagnostico-seo-gratis" },
    en: { label: "Request an SEO diagnostic", to: "/en/free-seo-diagnosis" },
  },
  local: {
    es: { label: "Evaluar mi visibilidad local", to: "/diagnostico-seo-gratis" },
    en: { label: "Evaluate local visibility", to: "/en/free-seo-diagnosis" },
  },
  website: {
    es: { label: "Solicitar una evaluación del sitio web", to: "/contacto" },
    en: { label: "Request a website evaluation", to: "/en/contact" },
  },
  branding: {
    es: { label: "Solicitar una evaluación de marca", to: "/contacto" },
    en: { label: "Request a brand evaluation", to: "/en/contact" },
  },
  hispanic: {
    es: { label: "Evaluar la oportunidad en el mercado hispano", to: "/contacto" },
    en: { label: "Evaluate the Hispanic market opportunity", to: "/en/contact" },
  },
  amazon: {
    es: { label: "Evaluar la oportunidad de crecimiento en Amazon", to: "/contacto" },
    en: { label: "Evaluate Amazon growth opportunity", to: "/en/contact" },
  },
  googleAds: {
    es: { label: "Solicitar una revisión de campañas", to: "/contacto" },
    en: { label: "Request a campaign review", to: "/en/contact" },
  },
  aiSearch: {
    es: { label: "Evaluar mi visibilidad en AI Search", to: "/diagnostico-seo-gratis" },
    en: { label: "Evaluate AI Search visibility", to: "/en/free-seo-diagnosis" },
  },
  b2b: {
    es: { label: "Agendar una reunión inicial de 30 minutos", to: "/agendar-consultoria" },
    en: { label: "Book an initial 30-minute meeting", to: "/en/book-consultation" },
  },
};

export const getCta = (intent: ConversionIntent, language: Language) =>
  CTA_BY_INTENT[intent][language];

/** ---------------------------------------------------------------
 * 2. NODOS: rutas reales por tipo
 * --------------------------------------------------------------- */
interface GraphNode {
  id: string;
  es: { path: string; label: string; description: string };
  en: { path: string; label: string; description: string };
}

/** Servicios publicados (money pages). */
export const SERVICE_NODES: GraphNode[] = [
  {
    id: 'technical-seo',
    es: { path: '/servicios/seo-tecnico', label: 'SEO técnico', description: 'Rastreo, indexación, renderizado y arquitectura en sitios grandes.' },
    en: { path: '/en/services/technical-seo', label: 'Technical SEO', description: 'Crawling, indexing, rendering and architecture on large sites.' },
  },
  {
    id: 'seo-consultant',
    es: { path: '/consultor-seo', label: 'Consultor SEO', description: 'Criterio senior sobre qué priorizar y cómo medirlo.' },
    en: { path: '/en/seo-consultant', label: 'SEO consultant', description: 'Senior judgement on what to prioritize and how to measure it.' },
  },
  {
    id: 'seo-audit',
    es: { path: '/servicios/seo-diagnostico', label: 'Auditoría SEO', description: 'Diagnóstico con prioridades, no una lista de tareas.' },
    en: { path: '/en/services/seo-audit', label: 'SEO audit', description: 'A diagnosis with priorities, not a task list.' },
  },
  {
    id: 'seo-sem',
    es: { path: '/servicios/seo-sem-estrategias', label: 'Estrategia SEO y SEM', description: 'Cómo se reparte la inversión entre orgánico y pago.' },
    en: { path: '/en/services/seo-sem-strategies', label: 'SEO and SEM strategy', description: 'How investment is split between organic and paid.' },
  },
  {
    id: 'google-ads',
    es: { path: '/servicios/google-ads', label: 'Google Ads', description: 'Campañas medidas por negocio, no por clics.' },
    en: { path: '/en/services/google-ads', label: 'Google Ads', description: 'Campaigns measured by business outcome, not clicks.' },
  },
  {
    id: 'ai-search',
    es: { path: '/servicios/posicionamiento-llm', label: 'Visibilidad en AI Search', description: 'Señales técnicas y de contenido para buscadores generativos.' },
    en: { path: '/en/services/llm-positioning', label: 'AI Search visibility', description: 'Technical and content signals for generative search.' },
  },
  {
    id: 'amazon',
    es: { path: '/servicios/amazon-consulting', label: 'Consultoría Amazon', description: 'Categoría, listings, Brand Registry y Amazon Ads.' },
    en: { path: '/en/services/amazon-consulting', label: 'Amazon consulting', description: 'Category, listings, Brand Registry and Amazon Ads.' },
  },
  {
    id: 'ecommerce-seo',
    es: { path: '/servicios/ecommerce-seo', label: 'Ecommerce SEO', description: 'Categorías, fichas de producto y navegación facetada.' },
    en: { path: '/en/services/ecommerce-seo', label: 'Ecommerce SEO', description: 'Categories, product pages and faceted navigation.' },
  },
  {
    id: 'international-seo',
    es: { path: '/servicios/seo-internacional', label: 'SEO internacional', description: 'Estructura multi-mercado, hreflang y demanda por país.' },
    en: { path: '/en/services/international-seo', label: 'International SEO', description: 'Multi-market structure, hreflang and demand by country.' },
  },
  {
    id: 'hispanic-seo',
    es: { path: '/servicios/seo-bilingue-mercado-hispano', label: 'SEO bilingüe y mercado hispano', description: 'Cómo busca en español el consumidor hispano de EE. UU.' },
    en: { path: '/en/services/hispanic-market-seo', label: 'Bilingual and Hispanic market SEO', description: 'How the US Hispanic consumer searches in Spanish.' },
  },
  {
    id: 'rebranding-seo',
    es: { path: '/servicios/rebranding-seo', label: 'Rediseño y rebranding con SEO', description: 'Cambiar sitio o marca sin perder lo que ya funciona.' },
    en: { path: '/en/services/rebranding-seo', label: 'Redesign and rebranding with SEO', description: 'Changing site or brand without losing what already works.' },
  },
  {
    id: 'branding',
    es: { path: '/servicios/branding', label: 'Branding y estrategia de marca', description: 'Posicionamiento, identidad, packaging y aplicaciones.' },
    en: { path: '/en/services/branding', label: 'Brand strategy and identity', description: 'Positioning, identity, packaging and applications.' },
  },
  {
    id: 'website-redesign',
    es: { path: '/servicios/rediseno-web', label: 'Rediseño web', description: 'Estrategia de sitio, arquitectura, conversión y desarrollo.' },
    en: { path: '/en/services/website-redesign', label: 'Website redesign', description: 'Site strategy, architecture, conversion and development.' },
  },
  {
    id: 'content-conversion',
    es: { path: '/servicios/contenido-conversion', label: 'Contenido para conversión', description: 'Contenido que responde a la decisión de compra.' },
    en: { path: '/en/services/content-conversion', label: 'Content for conversion', description: 'Content that answers the buying decision.' },
  },
];

/** Artículos publicados de la Search Library. */
export const ARTICLE_NODES: GraphNode[] = [
  {
    id: 'audit-vs-strategy',
    es: { path: '/biblioteca/auditoria-seo-o-estrategia-seo', label: '¿Auditoría SEO o estrategia SEO?', description: 'Qué resuelve cada una y cuándo pedir la otra.' },
    en: { path: '/en/library/seo-audit-vs-seo-strategy', label: 'SEO audit or SEO strategy?', description: 'What each one solves and when to ask for the other.' },
  },
  {
    id: 'technical-prioritization',
    es: { path: '/biblioteca/priorizacion-seo-tecnico', label: 'Priorización en SEO técnico', description: 'Qué arreglos mueven la aguja y cuáles no.' },
    en: { path: '/en/library/technical-seo-prioritization', label: 'Technical SEO prioritization', description: 'Which fixes move the needle and which do not.' },
  },
  {
    id: 'seo-for-ai-search',
    es: { path: '/biblioteca/seo-para-ai-search', label: 'SEO para AI Search', description: 'Qué cambia y qué no cuando responde una IA.' },
    en: { path: '/en/library/seo-for-ai-search', label: 'SEO for AI Search', description: 'What changes and what does not when an AI answers.' },
  },
  {
    id: 'geo-aeo-ai-seo',
    es: { path: '/biblioteca/geo-aeo-y-ai-seo', label: 'GEO, AEO y AI SEO', description: 'Los términos, sin promesas de recomendación.' },
    en: { path: '/en/library/geo-aeo-and-ai-seo', label: 'GEO, AEO and AI SEO', description: 'The terms, without recommendation promises.' },
  },
  {
    id: 'keywords-international',
    es: { path: '/biblioteca/traducir-keywords-no-es-seo-internacional', label: 'Traducir keywords no es SEO internacional', description: 'Por qué la demanda no se traduce.' },
    en: { path: '/en/library/translating-keywords-is-not-international-seo', label: 'Translating keywords is not international SEO', description: 'Why demand does not translate.' },
  },
  {
    id: 'hispanic-market',
    es: { path: '/biblioteca/seo-mercado-hispano-estados-unidos', label: 'SEO para el mercado hispano de EE. UU.', description: 'Búsqueda bilingüe y comportamiento real.' },
    en: { path: '/en/library/seo-us-hispanic-market', label: 'SEO for the US Hispanic market', description: 'Bilingual search and real behaviour.' },
  },
  {
    id: 'category-vs-product',
    es: { path: '/biblioteca/ecommerce-categorias-vs-productos', label: 'Categorías vs. productos en ecommerce', description: 'Dónde compite realmente un catálogo.' },
    en: { path: '/en/library/ecommerce-category-vs-product-pages', label: 'Category vs. product pages', description: 'Where a catalog actually competes.' },
  },
  {
    id: 'amazon-seo-vs-ads',
    es: { path: '/biblioteca/amazon-seo-vs-amazon-ads', label: 'Amazon SEO vs. Amazon Ads', description: 'Cómo se relacionan orgánico y publicidad.' },
    en: { path: '/en/library/amazon-seo-vs-amazon-advertising', label: 'Amazon SEO vs. Amazon Advertising', description: 'How organic and advertising relate.' },
  },
  {
    id: 'consultant-vs-agency',
    es: { path: '/biblioteca/consultor-seo-o-agencia-seo', label: '¿Consultor SEO o agencia SEO?', description: 'Cómo elegir según lo que le falta a tu empresa.' },
    en: { path: '/en/library/seo-consultant-vs-seo-agency', label: 'SEO consultant or SEO agency?', description: 'How to choose based on what your company lacks.' },
  },
];

const nodeById = (nodes: GraphNode[], id: string) => nodes.find((n) => n.id === id);

/** ---------------------------------------------------------------
 * 3. HUBS TEMÁTICOS (áreas de expertise aprobadas)
 * --------------------------------------------------------------- */
export interface KnowledgeHub {
  id: string;
  es: { name: string; path: string };
  en: { name: string; path: string };
  intent: ConversionIntent;
  /** IDs de SERVICE_NODES relacionados (excluye el propio hub). */
  services: string[];
  /** IDs de ARTICLE_NODES relacionados. */
  articles: string[];
  /** Slugs de casos publicados que sirven como evidencia. */
  cases: string[];
  /** Rutas de páginas locales asociadas. */
  locations: string[];
  /** Huecos conocidos: se documentan, no se rellenan con contenido inventado. */
  gaps: string[];
}

export const KNOWLEDGE_HUBS: KnowledgeHub[] = [
  {
    id: 'technical-seo',
    es: { name: 'SEO técnico', path: '/servicios/seo-tecnico' },
    en: { name: 'Technical SEO', path: '/en/services/technical-seo' },
    intent: 'seo',
    services: ['seo-audit', 'rebranding-seo', 'ecommerce-seo', 'ai-search'],
    articles: ['technical-prioritization', 'audit-vs-strategy', 'seo-for-ai-search'],
    cases: [],
    locations: [],
    gaps: [
      'Sin caso técnico publicable: no se inventa uno.',
      'Falta contenido propio sobre migraciones y Core Web Vitals (ver backlog).',
    ],
  },
  {
    id: 'local-seo',
    es: { name: 'SEO local', path: '/seo-local' },
    en: { name: 'Local SEO', path: '/en/local-seo' },
    intent: 'local',
    services: ['seo-consultant', 'google-ads', 'seo-audit'],
    articles: ['audit-vs-strategy'],
    cases: [],
    locations: [
      '/seo-houston', '/seo-the-woodlands', '/seo-spring', '/seo-conroe',
      '/seo-tomball', '/seo-magnolia', '/seo-katy', '/seo-sugar-land', '/seo-memorial',
    ],
    gaps: [
      'Sin caso local documentado con permiso: NEEDS LOCAL RESEARCH en las 16 páginas.',
      'No existe artículo de Local Search en la Search Library.',
    ],
  },
  {
    id: 'international-seo',
    es: { name: 'SEO internacional', path: '/servicios/seo-internacional' },
    en: { name: 'International SEO', path: '/en/services/international-seo' },
    intent: 'seo',
    services: ['technical-seo', 'ecommerce-seo', 'hispanic-seo'],
    articles: ['keywords-international', 'hispanic-market'],
    cases: ['marca-abrasivos-amazon-usa'],
    locations: [],
    gaps: [],
  },
  {
    id: 'google-ads',
    es: { name: 'Google Ads y performance', path: '/servicios/google-ads' },
    en: { name: 'Google Ads and performance', path: '/en/services/google-ads' },
    intent: 'googleAds',
    services: ['seo-sem', 'content-conversion', 'seo-consultant'],
    articles: ['audit-vs-strategy'],
    cases: [],
    locations: [
      '/google-ads-houston', '/google-ads-the-woodlands', '/google-ads-spring',
      '/google-ads-conroe', '/google-ads-tomball', '/google-ads-magnolia',
      '/google-ads-katy', '/google-ads-sugar-land', '/google-ads-memorial',
    ],
    gaps: ['Sin artículo publicado sobre SEO vs. Google Ads (BOFU prioritario en el backlog).'],
  },
  {
    id: 'ecommerce-amazon',
    es: { name: 'Amazon y ecommerce', path: '/servicios/amazon-consulting' },
    en: { name: 'Amazon and ecommerce', path: '/en/services/amazon-consulting' },
    intent: 'amazon',
    services: ['ecommerce-seo', 'international-seo', 'content-conversion'],
    articles: ['amazon-seo-vs-ads', 'category-vs-product'],
    cases: ['marca-abrasivos-amazon-usa'],
    locations: ['/ecommerce-marketplaces-the-woodlands'],
    gaps: [],
  },
  {
    id: 'hispanic-market',
    es: { name: 'Mercado hispano', path: '/servicios/seo-bilingue-mercado-hispano' },
    en: { name: 'Hispanic market', path: '/en/services/hispanic-market-seo' },
    intent: 'hispanic',
    services: ['international-seo', 'content-conversion', 'rebranding-seo'],
    articles: ['hispanic-market', 'keywords-international'],
    cases: ['el-venado-foods-branding-ecommerce'],
    locations: ['/seo-houston'],
    gaps: [
      'El caso de la empresa de bebidas permanece sin publicar (identidad sin autorización).',
      'Falta contenido sobre packaging y comportamiento de consumo (backlog).',
    ],
  },
  {
    id: 'website-strategy',
    es: { name: 'Estrategia y rediseño web', path: '/servicios/rediseno-web' },
    en: { name: 'Website strategy and redesign', path: '/en/services/website-redesign' },
    intent: 'website',
    services: ['website-redesign', 'technical-seo', 'content-conversion', 'rebranding-seo'],
    articles: ['technical-prioritization', 'audit-vs-strategy'],
    cases: ['el-venado-foods-branding-ecommerce'],
    locations: ['/branding-web-the-woodlands'],
    gaps: [
      'Sin contenido publicado sobre coste de rediseño ni rediseño vs. refresh (backlog BOFU).',
    ],
  },
  {
    id: 'branding',
    es: { name: 'Marca e identidad', path: '/servicios/branding' },
    en: { name: 'Brand strategy and identity', path: '/en/services/branding' },
    intent: 'branding',
    services: ['branding', 'hispanic-seo', 'website-redesign'],
    articles: ['hispanic-market'],
    cases: ['el-venado-foods-branding-ecommerce'],
    locations: ['/branding-web-the-woodlands'],
    gaps: [
      'El caso de branding para bebidas está retenido hasta obtener autorización.',
    ],
  },
  {
    id: 'ai-search',
    es: { name: 'Visibilidad en AI Search', path: '/servicios/posicionamiento-llm' },
    en: { name: 'AI Search visibility', path: '/en/services/llm-positioning' },
    intent: 'aiSearch',
    services: ['technical-seo', 'content-conversion', 'seo-consultant'],
    articles: ['seo-for-ai-search', 'geo-aeo-ai-seo', 'technical-prioritization'],
    cases: [],
    locations: [],
    gaps: ['Sin caso de evidencia: la visibilidad en sistemas de IA no se mide hoy con datos propios publicables.'],
  },
  {
    id: 'b2b-growth',
    es: { name: 'Crecimiento B2B', path: '/consultor-seo' },
    en: { name: 'B2B growth', path: '/en/seo-consultant' },
    intent: 'b2b',
    services: ['seo-audit', 'google-ads', 'technical-seo', 'content-conversion'],
    articles: ['audit-vs-strategy', 'technical-prioritization'],
    cases: ['marca-abrasivos-amazon-usa'],
    locations: ['/seo-houston'],
    gaps: [
      'CONTENT GAP: las 7 preguntas del comprador B2B (coste, plazos, consultor vs. agencia, medición) no tienen contenido publicado.',
    ],
  },
];

export const getHub = (id: string) => KNOWLEDGE_HUBS.find((h) => h.id === id);

/** ---------------------------------------------------------------
 * 4. PÁGINAS LOCALES: ficha de relación (sección 3 del brief)
 * `NEEDS LOCAL RESEARCH` = no hay evidencia local propia todavía.
 * --------------------------------------------------------------- */
export interface LocationNode {
  path: string;
  city: string;
  /** Relación con el mercado principal de Houston. */
  relationToHouston: 'primary' | 'submarket' | 'metro' | 'out-of-state';
  hub: string;
  service: string;
  needsLocalResearch: boolean;
}

export const LOCATION_NODES: LocationNode[] = [
  { path: '/seo-houston', city: 'Houston', relationToHouston: 'primary', hub: 'local-seo', service: 'seo-consultant', needsLocalResearch: false },
  { path: '/seo-the-woodlands', city: 'The Woodlands', relationToHouston: 'submarket', hub: 'local-seo', service: 'seo-consultant', needsLocalResearch: true },
  { path: '/seo-spring', city: 'Spring', relationToHouston: 'submarket', hub: 'local-seo', service: 'seo-consultant', needsLocalResearch: true },
  { path: '/seo-conroe', city: 'Conroe', relationToHouston: 'submarket', hub: 'local-seo', service: 'seo-consultant', needsLocalResearch: true },
  { path: '/seo-tomball', city: 'Tomball', relationToHouston: 'submarket', hub: 'local-seo', service: 'seo-consultant', needsLocalResearch: true },
  { path: '/seo-magnolia', city: 'Magnolia', relationToHouston: 'submarket', hub: 'local-seo', service: 'seo-consultant', needsLocalResearch: true },
  { path: '/seo-katy', city: 'Katy', relationToHouston: 'metro', hub: 'local-seo', service: 'seo-consultant', needsLocalResearch: true },
  { path: '/seo-sugar-land', city: 'Sugar Land', relationToHouston: 'metro', hub: 'local-seo', service: 'seo-consultant', needsLocalResearch: true },
  { path: '/seo-memorial', city: 'Memorial (Houston)', relationToHouston: 'metro', hub: 'local-seo', service: 'seo-consultant', needsLocalResearch: true },
  { path: '/google-ads-houston', city: 'Houston', relationToHouston: 'primary', hub: 'google-ads', service: 'google-ads', needsLocalResearch: false },
  { path: '/google-ads-the-woodlands', city: 'The Woodlands', relationToHouston: 'submarket', hub: 'google-ads', service: 'google-ads', needsLocalResearch: true },
  { path: '/google-ads-spring', city: 'Spring', relationToHouston: 'submarket', hub: 'google-ads', service: 'google-ads', needsLocalResearch: true },
  { path: '/google-ads-conroe', city: 'Conroe', relationToHouston: 'submarket', hub: 'google-ads', service: 'google-ads', needsLocalResearch: true },
  { path: '/google-ads-tomball', city: 'Tomball', relationToHouston: 'submarket', hub: 'google-ads', service: 'google-ads', needsLocalResearch: true },
  { path: '/google-ads-magnolia', city: 'Magnolia', relationToHouston: 'submarket', hub: 'google-ads', service: 'google-ads', needsLocalResearch: true },
  { path: '/google-ads-katy', city: 'Katy', relationToHouston: 'metro', hub: 'google-ads', service: 'google-ads', needsLocalResearch: true },
  { path: '/google-ads-sugar-land', city: 'Sugar Land', relationToHouston: 'metro', hub: 'google-ads', service: 'google-ads', needsLocalResearch: true },
  { path: '/google-ads-memorial', city: 'Memorial (Houston)', relationToHouston: 'metro', hub: 'google-ads', service: 'google-ads', needsLocalResearch: true },
  { path: '/branding-web-the-woodlands', city: 'The Woodlands', relationToHouston: 'submarket', hub: 'branding', service: 'rebranding-seo', needsLocalResearch: true },
  { path: '/ecommerce-marketplaces-the-woodlands', city: 'The Woodlands', relationToHouston: 'submarket', hub: 'ecommerce-amazon', service: 'amazon', needsLocalResearch: true },
  { path: '/social-media-marketing-the-woodlands', city: 'The Woodlands', relationToHouston: 'submarket', hub: 'b2b-growth', service: 'content-conversion', needsLocalResearch: true },
  { path: '/seo-orlando', city: 'Orlando', relationToHouston: 'out-of-state', hub: 'local-seo', service: 'seo-consultant', needsLocalResearch: true },
  { path: '/seo-miami', city: 'Miami', relationToHouston: 'out-of-state', hub: 'hispanic-market', service: 'hispanic-seo', needsLocalResearch: true },
  { path: '/google-ads-orlando', city: 'Orlando', relationToHouston: 'out-of-state', hub: 'google-ads', service: 'google-ads', needsLocalResearch: true },
  { path: '/google-ads-miami', city: 'Miami', relationToHouston: 'out-of-state', hub: 'google-ads', service: 'google-ads', needsLocalResearch: true },
];

export const getLocationNode = (path: string) => LOCATION_NODES.find((l) => l.path === path);

/** ---------------------------------------------------------------
 * 5. CONSTRUCCIÓN DE ENLACES EDITORIALES
 * Devuelve enlaces reales: hub padre, servicio, artículo y caso (si existe).
 * --------------------------------------------------------------- */
const CASE_LABELS: Record<string, { es: string; en: string; desc: { es: string; en: string } }> = {
  'marca-abrasivos-amazon-usa': {
    es: 'Caso: marca de abrasivos industriales en Amazon USA',
    en: 'Case: industrial abrasives brand on Amazon USA',
    desc: {
      es: 'Investigación de categoría, listings, Brand Registry y Amazon Ads. Resultado de este caso, no una promesa general.',
      en: 'Category research, listings, Brand Registry and Amazon Ads. Result of this engagement, not a general promise.',
    },
  },
  'el-venado-foods-branding-ecommerce': {
    es: 'Caso: El Venado Foods — marca, ecommerce y mercado local',
    en: 'Case: El Venado Foods — brand, ecommerce and local market',
    desc: {
      es: 'Marca, packaging, punto de venta y ecommerce para el consumidor hispano en Houston.',
      en: 'Brand, packaging, point of sale and ecommerce for the Hispanic consumer in Houston.',
    },
  },
};

const caseLink = (slug: string, language: Language): MoneyPageLink | null => {
  const meta = CASE_LABELS[slug];
  if (!meta) return null;
  return {
    label: language === 'es' ? meta.es : meta.en,
    description: language === 'es' ? meta.desc.es : meta.desc.en,
    to: language === 'es' ? `/casos-de-exito/${slug}` : `/en/case-studies/${slug}`,
  };
};

const toLink = (node: GraphNode | undefined, language: Language): MoneyPageLink | null => {
  if (!node) return null;
  const loc = node[language];
  return { label: loc.label, description: loc.description, to: loc.path };
};

/**
 * Enlaces contextuales para una página que pertenece a un hub:
 * 1 hub padre + 2-4 relacionados + 1 caso (si existe) + 1 recurso.
 */
export const getHubConnections = (hubId: string, language: Language, options?: { includeHub?: boolean }): MoneyPageLink[] => {
  const hub = getHub(hubId);
  if (!hub) return [];
  const links: (MoneyPageLink | null)[] = [];

  if (options?.includeHub !== false) {
    links.push({
      label: hub[language].name,
      description: language === 'es'
        ? 'Hub principal de esta área de trabajo.'
        : 'Main hub for this area of work.',
      to: hub[language].path,
    });
  }

  hub.services.slice(0, 3).forEach((id) => links.push(toLink(nodeById(SERVICE_NODES, id), language)));
  hub.cases.slice(0, 1).forEach((slug) => links.push(caseLink(slug, language)));
  hub.articles.slice(0, 2).forEach((id) => links.push(toLink(nodeById(ARTICLE_NODES, id), language)));

  const seen = new Set<string>();
  return links.filter((l): l is MoneyPageLink => {
    if (!l || seen.has(l.to)) return false;
    seen.add(l.to);
    return true;
  });
};

/** Enlaces para una página local: hub local, servicio, Houston y caso relacionado. */
export const getLocationConnections = (path: string, language: Language): MoneyPageLink[] => {
  const node = getLocationNode(path);
  if (!node) return [];
  const links = getHubConnections(node.hub, language);
  const service = toLink(nodeById(SERVICE_NODES, node.service), language);
  if (service && !links.some((l) => l.to === service.to)) links.unshift(service);
  if (node.city === 'The Woodlands') {
    const woodlands: MoneyPageLink[] = [
      {
        label: language === 'es' ? 'Marca y sitio web en The Woodlands' : 'Brand and website in The Woodlands',
        description: language === 'es' ? 'Identidad y sitio para empresas del submercado norte.' : 'Identity and website for companies in the north submarket.',
        to: '/branding-web-the-woodlands',
      },
      {
        label: language === 'es' ? 'Ecommerce y marketplaces en The Woodlands' : 'Ecommerce and marketplaces in The Woodlands',
        description: language === 'es' ? 'Catálogo propio y venta en marketplaces.' : 'Own catalog and marketplace selling.',
        to: '/ecommerce-marketplaces-the-woodlands',
      },
      {
        label: language === 'es' ? 'Social media en The Woodlands' : 'Social media in The Woodlands',
        description: language === 'es' ? 'Presencia social conectada con la demanda local.' : 'Social presence connected to local demand.',
        to: '/social-media-marketing-the-woodlands',
      },
    ];
    woodlands.forEach((l) => {
      if (l.to !== node.path && !links.some((x) => x.to === l.to)) links.push(l);
    });
  }

  if (node.relationToHouston !== 'primary' && node.path.startsWith('/seo-')) {
    links.push({
      label: language === 'es' ? 'SEO local en Houston' : 'Local SEO in Houston',
      description: language === 'es'
        ? 'El mercado principal del que depende este submercado.'
        : 'The primary market this submarket belongs to.',
      to: language === 'es' ? '/seo-local-houston' : '/en/local-seo-houston',
    });
  }
  return links.slice(0, 8);
};

/** Caso → hub temático principal al que sirve como evidencia. */
export const CASE_HUB_MAP: Record<string, string> = {
  'marca-abrasivos-amazon-usa': 'ecommerce-amazon',
  'el-venado-foods-branding-ecommerce': 'hispanic-market',
  'el-nino-aguas-frescas-branding': 'branding',
  'bristol-myers-squibb-sicodi': 'b2b-growth',
};

/** ---------------------------------------------------------------
 * 6. TOPIC MAP (capa de conocimiento canónica)
 * Mapa determinista tema → servicio canónico → artículos de la Search
 * Library → fases del ROC Search Growth Framework (1-7). Solo temas con
 * página real publicada. Lo consumen el bloque "Fuentes y contexto" de los
 * artículos y scripts/validate-knowledge-layer.ts.
 * --------------------------------------------------------------- */
export interface KnowledgeTopic {
  id: string;
  nameEs: string;
  nameEn: string;
  canonicalServiceUrlEs: string;
  canonicalServiceUrlEn: string;
  /** IDs de `insights` (src/data/insights). */
  supportingArticles: string[];
  methodologyPhase: number[];
  relatedTopics: string[];
}

export const KNOWLEDGE_TOPICS: KnowledgeTopic[] = [
  { id: 'seo-consulting', nameEs: 'Consultoría SEO', nameEn: 'SEO consulting', canonicalServiceUrlEs: '/consultor-seo', canonicalServiceUrlEn: '/en/seo-consultant', supportingArticles: ['consultant-vs-agency'], methodologyPhase: [1], relatedTopics: ['technical-seo'] },
  { id: 'technical-seo', nameEs: 'SEO técnico', nameEn: 'Technical SEO', canonicalServiceUrlEs: '/servicios/seo-tecnico', canonicalServiceUrlEn: '/en/services/technical-seo', supportingArticles: ['technical-prioritization', 'audit-vs-strategy'], methodologyPhase: [2], relatedTopics: ['ai-search', 'ecommerce-seo'] },
  { id: 'international-seo', nameEs: 'SEO internacional', nameEn: 'International SEO', canonicalServiceUrlEs: '/servicios/seo-internacional', canonicalServiceUrlEn: '/en/services/international-seo', supportingArticles: ['keywords-not-international'], methodologyPhase: [1, 3], relatedTopics: ['hispanic-market-seo'] },
  { id: 'local-seo', nameEs: 'SEO local', nameEn: 'Local SEO', canonicalServiceUrlEs: '/seo-local', canonicalServiceUrlEn: '/en/local-seo', supportingArticles: [], methodologyPhase: [3, 5], relatedTopics: ['google-ads'] },
  { id: 'hispanic-market-seo', nameEs: 'SEO para el mercado hispano', nameEn: 'Hispanic market SEO', canonicalServiceUrlEs: '/servicios/seo-bilingue-mercado-hispano', canonicalServiceUrlEn: '/en/services/hispanic-market-seo', supportingArticles: ['hispanic-market-seo'], methodologyPhase: [1, 3], relatedTopics: ['international-seo'] },
  { id: 'ecommerce-seo', nameEs: 'Ecommerce SEO', nameEn: 'Ecommerce SEO', canonicalServiceUrlEs: '/servicios/ecommerce-seo', canonicalServiceUrlEn: '/en/services/ecommerce-seo', supportingArticles: ['category-vs-product'], methodologyPhase: [2, 3], relatedTopics: ['amazon-seo', 'technical-seo'] },
  { id: 'amazon-seo', nameEs: 'Amazon SEO', nameEn: 'Amazon SEO', canonicalServiceUrlEs: '/servicios/amazon-consulting', canonicalServiceUrlEn: '/en/services/amazon-consulting', supportingArticles: ['amazon-seo-vs-ads'], methodologyPhase: [3, 6], relatedTopics: ['ecommerce-seo'] },
  { id: 'ai-search', nameEs: 'Visibilidad en AI Search', nameEn: 'AI Search visibility', canonicalServiceUrlEs: '/servicios/posicionamiento-llm', canonicalServiceUrlEn: '/en/services/llm-positioning', supportingArticles: ['seo-for-ai-search', 'geo-aeo-ai-seo'], methodologyPhase: [4, 5], relatedTopics: ['technical-seo'] },
  { id: 'google-ads', nameEs: 'Google Ads', nameEn: 'Google Ads', canonicalServiceUrlEs: '/servicios/google-ads', canonicalServiceUrlEn: '/en/services/google-ads', supportingArticles: [], methodologyPhase: [6, 7], relatedTopics: ['local-seo'] },
  { id: 'branding', nameEs: 'Branding', nameEn: 'Branding', canonicalServiceUrlEs: '/servicios/branding', canonicalServiceUrlEn: '/en/services/branding', supportingArticles: [], methodologyPhase: [4], relatedTopics: ['website-redesign'] },
  { id: 'website-redesign', nameEs: 'Rediseño web', nameEn: 'Website redesign', canonicalServiceUrlEs: '/servicios/rediseno-web', canonicalServiceUrlEn: '/en/services/website-redesign', supportingArticles: [], methodologyPhase: [2, 6], relatedTopics: ['technical-seo', 'content-conversion'] },
  { id: 'content-conversion', nameEs: 'Contenido y conversión', nameEn: 'Content and conversion', canonicalServiceUrlEs: '/servicios/contenido-conversion', canonicalServiceUrlEn: '/en/services/content-conversion', supportingArticles: [], methodologyPhase: [3, 6], relatedTopics: ['website-redesign'] },
];

export const topicForArticle = (articleId: string) =>
  KNOWLEDGE_TOPICS.find((t) => t.supportingArticles.includes(articleId));
